import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import i18n from '@i18n/config';

// =============================================================================
// Type Definitions
// =============================================================================

/**
 * Structured error response from backend (Option B i18n format)
 */
export interface ApiError {
  code: string;
  description: string;
  metadata?: Record<string, unknown>;
}

/**
 * Translates an API error using i18n with metadata interpolation.
 * Uses the error code directly as the i18n key (e.g., "Vote.TooManyFailedAttempts").
 * Falls back to the backend description if no translation is found.
 */
export function translateApiError(error: ApiError): string {
  const i18nKey = `errors:${error.code}`;

  if (i18n.exists(i18nKey)) {
    return i18n.t(i18nKey as never, error.metadata ?? {});
  }

  return error.description;
}

/**
 * Extracts the first error from a structured error response.
 * Supports both new structured format and legacy "Code:Description" format.
 */
export function extractFirstApiError(
  errors: ApiError[] | Record<string, string[] | string>
): ApiError | undefined {
  // New structured format: array of ApiError
  if (Array.isArray(errors) && errors.length > 0) {
    const first: unknown = errors[0];
    if (first && typeof first === 'object' && 'code' in first) {
      return first as ApiError;
    }
  }

  // Legacy format: Record<string, string[]>
  if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
    const record = errors;
    const firstKey = Object.keys(record)[0];
    if (firstKey) {
      const errorValue = record[firstKey];
      let message: string | undefined;

      if (Array.isArray(errorValue) && errorValue.length > 0) {
        message = errorValue[0];
      } else if (typeof errorValue === 'string') {
        message = errorValue;
      }

      if (message) {
        // Skip parsing for framework error keys like "$" or "dto" - use message as-is
        if (firstKey === '$' || firstKey === 'dto') {
          return { code: 'ValidationError', description: message };
        }

        // Legacy format: "ErrorCode:Description"
        const colonIndex = message.indexOf(':');
        if (colonIndex > 0 && colonIndex < message.length - 1) {
          return {
            code: message.substring(0, colonIndex).trim(),
            description: message.substring(colonIndex + 1).trim(),
          };
        }
        return { code: 'Unknown', description: message };
      }
    }
  }

  return undefined;
}

/**
 * Extracts the first error message from a validation error response
 * @param errors - The errors object from the server response
 * @returns The first error message found (translated if available), or undefined if none found
 */
export function extractFirstErrorMessage(errors: Record<string, string[] | string> | ApiError[]): string | undefined {
  const apiError = extractFirstApiError(errors);
  if (apiError) {
    return translateApiError(apiError);
  }
  return undefined;
}

/**
 * Extracts a user-friendly error message from an API exception
 * @param err - The error object from a catch block (typically from RTK Query)
 * @param defaultMessage - The default message to return if extraction fails
 * @returns The extracted and translated error message or the default message
 */
export function extractErrorMessageFromException(err: unknown, defaultMessage: string): string {
  // Extract friendly error message from server response
  if (err && typeof err === 'object' && 'data' in err) {
    const errorData = (err as FetchBaseQueryError).data as {
      errors?: ApiError[] | Record<string, string[] | string>;
    };

    if (errorData?.errors) {
      const extractedMessage = extractFirstErrorMessage(errorData.errors);
      if (extractedMessage) {
        return extractedMessage;
      }
    }
  }

  return defaultMessage;
}

/**
 * Extracts all API errors from an exception.
 * Useful when you need access to error codes and metadata.
 */
export function extractApiErrorsFromException(err: unknown): ApiError[] {
  if (err && typeof err === 'object' && 'data' in err) {
    const errorData = (err as FetchBaseQueryError).data as {
      errors?: ApiError[] | Record<string, string[] | string>;
    };

    if (Array.isArray(errorData?.errors)) {
      return errorData.errors.filter(
        (e): e is ApiError => typeof e === 'object' && e !== null && 'code' in e
      );
    }

    // Convert legacy format to ApiError[]
    if (errorData?.errors && typeof errorData.errors === 'object') {
      const result: ApiError[] = [];
      const apiError = extractFirstApiError(errorData.errors);
      if (apiError) {
        result.push(apiError);
      }
      return result;
    }
  }

  return [];
}

// =============================================================================
// Simple Error Code Utilities (from errorUtils.ts)
// =============================================================================

/**
 * Safely check if an error array contains a specific error code.
 * Handles cases where errors might be objects instead of strings.
 */
export function hasErrorCode(errors: unknown[] | undefined, code: string): boolean {
  return errors?.some((e) => typeof e === 'string' && e.includes(code)) ?? false;
}

/**
 * Find and return the first error string matching a code.
 * Returns undefined if not found.
 */
export function findErrorByCode(errors: unknown[] | undefined, code: string): string | undefined {
  return errors?.find((e): e is string => typeof e === 'string' && e.includes(code));
}

// =============================================================================
// RTK Query Error Utilities (from Errors.ts)
// =============================================================================

interface RTKQueryError {
  data?: {
    errors?: Record<string, string[] | string>;
  };
}

function isRTKQueryError(err: unknown): err is RTKQueryError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'data' in err &&
    typeof (err as RTKQueryError).data === 'object'
  );
}

/**
 * Maps an error code to a message using a provided mapping.
 * Falls back to default message if code is not found in the map.
 */
export function tryMapErrorMessage(errorCode: string, defaultMsg: string, errorMap: Record<string, string>) {
  return errorMap[errorCode] ?? defaultMsg;
}

/**
 * Extracts the first error code from an RTK Query error response.
 * Returns 'UnknownError' if extraction fails.
 */
export function extractFirstErrorCode(err: unknown): string {
  if (!isRTKQueryError(err)) {
    return 'UnknownError';
  }

  const errors = err.data?.errors;
  if (!errors) {
    return 'UnknownError';
  }

  const firstKey = Object.keys(errors)[0];
  if (!firstKey) {
    return 'UnknownError';
  }

  const errorValue = errors[firstKey];
  let firstError: string | undefined;

  if (Array.isArray(errorValue) && errorValue.length > 0) {
    firstError = errorValue[0];
  } else if (typeof errorValue === 'string') {
    firstError = errorValue;
  }

  if (typeof firstError === 'string') {
    // Split by ':' to get the error code part (e.g., "Vote.DejaVote: message" -> "Vote.DejaVote")
    return firstError.split(':')[0].trim();
  }

  return 'UnknownError';
}
